import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Table,
  message,
  Image,
  Tag,
  Space,
  Typography,
  Form,
  Select,
  Input,
  Col,
} from "antd";
import { EyeOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import {
  StoreDisplayGroupPlanogram,
  StoreDisplayGroupPlanogramFilters,
  PlanogramPaginationParams,
} from "../../../types/storeDisplayGroupPlanogram";
import { StringOption } from "../../../types/index";
import StoreDisplayGroupPlanogramService from "../../../services/storeDisplayGroupPlanogramService";
import HQDisplayGroupService from "../../../services/hqDisplayGroupService";
import StoreGroupService from "../../../services/storeGroupService";
import { warehouseService } from "../../../services/warehouseService";
import categoryService from "../../../services/categoryService";
import FilterPanel from "../../../components/common/FilterPanel";
import EmptyState from "../../../components/common/EmptyState";
import NetworkError from "../../../components/common/NetworkError";
import { handleApiResponse } from "../../../utils/responseTransformer";
import {
  isRetryableError,
  sanitizeErrorForDisplay,
} from "../../../utils/errorHandler";
import dayjs from "dayjs";

const { Title } = Typography;

interface StoreOption {
  code: string;
  name: string;
}

interface StoreGroupOption {
  code: string;
  name: string;
}

interface DisplayGroupOption {
  code: string;
  name: string;
}

interface DivisionOption {
  value: string;
  label: string;
}

const StoreDisplayGroupPlanogramList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(true);
  const [planogramData, setPlanogramData] = useState<
    StoreDisplayGroupPlanogram[]
  >([]);
  const [total, setTotal] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // 选项数据
  const [storeOptions, setStoreOptions] = useState<StoreOption[]>([]);
  const [storeGroupOptions, setStoreGroupOptions] = useState<
    StoreGroupOption[]
  >([]);
  const [displayGroupOptions, setDisplayGroupOptions] = useState<
    DisplayGroupOption[]
  >([]);
  const [divisionOptions, setDivisionOptions] = useState<DivisionOption[]>([]);

  // 分页和筛选状态
  const [pagination, setPagination] = useState<PlanogramPaginationParams>({
    page: 1,
    pageSize: 20,
    sortBy: "gmtModified",
    sortOrder: "desc",
  });

  const [filters, setFilters] = useState<StoreDisplayGroupPlanogramFilters>({});

  // 初始化数据加载
  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadPlanogramData();
  }, [filters, pagination]);

  // 加载初始数据（选项数据）
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadStoreOptions(),
        loadStoreGroupOptions(),
        loadDisplayGroupOptions(),
        loadDivisionOptions(),
      ]);
    } catch (error) {
      console.error("Failed to load initial data:", error);
      setError("加载初始数据失败");
    } finally {
      setLoading(false);
    }
  }, []);

  // 加载门店选项
  const loadStoreOptions = async () => {
    try {
      const response = await warehouseService.listWarehouse({ searchStr: "" });
      if (response.success && response.data) {
        const stores = response.data.map((warehouse: any) => ({
          code: warehouse.code,
          name: `${warehouse.code} - ${warehouse.name}`,
        }));
        setStoreOptions(stores);
      }
    } catch (error) {
      console.error("Failed to load stores:", error);
    }
  };

  // 加载店组选项
  const loadStoreGroupOptions = async () => {
    try {
      const response = await StoreGroupService.getStoreGroups(
        {},
        { page: 1, pageSize: 1000 }
      );
      if (response.success && response.data?.tableData) {
        const storeGroups = response.data.tableData.map((group: any) => ({
          code: group.code,
          name: `${group.code} - ${group.name}`,
        }));
        setStoreGroupOptions(storeGroups);
      }
    } catch (error) {
      console.error("Failed to load store groups:", error);
    }
  };

  // 加载DG选项
  const loadDisplayGroupOptions = async () => {
    try {
      const response = await HQDisplayGroupService.getDisplayGroups(
        {},
      );
      if (response.success && response.data) {
        const displayGroups = response.data?.map((dg: any) => ({
          code: dg.code,
          name: `${dg.code} - ${dg.name}`,
        }));
        setDisplayGroupOptions(displayGroups);
      }
    } catch (error) {
      console.error("Failed to load display groups:", error);
    }
  };

  // 加载事业部选项
  const loadDivisionOptions = async () => {
    try {
      const response = await categoryService.listDivision();
      if (response.success && response.data) {
        setDivisionOptions(response.data);
      }
    } catch (error) {
      console.error("Failed to load divisions:", error);
    }
  };

  // 加载陈列图数据
  const loadPlanogramData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      filters.source = "benchmark";
      const response = await StoreDisplayGroupPlanogramService.getPlanogramList(
        filters,
        pagination
      );
      console.log("Planogram data response:", response);

      if (response.success && response.data) {
        setPlanogramData(response.data.tableData || []);
        setTotal(response.data.paging?.totalCount || 0);
      } else {
        setPlanogramData([]);
        setTotal(0);
        if (response.errorMessage) {
          setError(response.errorMessage);
        }
      }
    } catch (error) {
      console.error("Failed to fetch planogram data:", error);
      const errorMessage = sanitizeErrorForDisplay(error);
      setError(errorMessage);
      setPlanogramData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination]);

  // 处理筛选变化
  const handleFiltersChange = useCallback((values: any) => {
    const newFilters: StoreDisplayGroupPlanogramFilters = {
      storeCode: values.storeCode,
      shopGroupCode: values.shopGroupCode,
      displayGroupCode: values.displayGroupCode,
      division: values.division,
      status: values.status,
      source: values.source,
      keyword: values.keyword,
    };

    // 移除值为 'all' 的过滤条件
    Object.keys(newFilters).forEach((key) => {
      const filterKey = key as keyof StoreDisplayGroupPlanogramFilters;
      if (newFilters[filterKey] === "all") {
        delete newFilters[filterKey];
      }
    });

    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // 处理重置筛选
  const handleReset = useCallback(() => {
    setFilters({});
    setPagination({
      page: 1,
      pageSize: 20,
      sortBy: "gmtModified",
      sortOrder: "desc",
    });
    return {};
  }, []);

  // 处理表格变化
  const handleTableChange = useCallback(
    (paginationConfig: any, _filters: any, sorter: any) => {
      const newPagination: PlanogramPaginationParams = {
        page: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      };

      if (sorter.field && sorter.order) {
        newPagination.sortBy = sorter.field;
        newPagination.sortOrder = sorter.order === "ascend" ? "asc" : "desc";
      }

      setPagination(newPagination);
    },
    []
  );

  // 处理查看详情
  const handleViewDetail = useCallback(
    (record: StoreDisplayGroupPlanogram) => {
      navigate(
        `/store-display-groups/${record.shopGroupCode}/planogram/${record.code}/list`
      );
    },
    [navigate]
  );

  // 处理重试
  const handleRetry = useCallback(() => {
    loadPlanogramData();
  }, [loadPlanogramData]);

  // 格式化时间
  const formatDateTime = (timestamp: string): string => {
    return timestamp ? dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss") : "-";
  };

  const getStatusKey = (status: string): string => {
    const statusMap: Record<string, string> = {
      '草稿': 'Draft',
      '审核中': 'PENDING',
      '审核拒绝': 'REJECTED',
      '生效中': 'ACTIVE',
      '已归档': 'ARCHIVED'
    };
    return statusMap[status] || status;
  };

  // 定义表格列
  const columns: ColumnsType<StoreDisplayGroupPlanogram> = [
    {
      title: t("storeDisplayGroupPlanogram.code"),
      dataIndex: "code",
      key: "code",
      // sorter: true,
      width: 120,
      sortOrder:
        pagination.sortBy === "code"
          ? pagination.sortOrder === "asc"
            ? "ascend"
            : "descend"
          : null,
      fixed: "left",
    },
    {
      title: t("storeDisplayGroup.shop"),
      dataIndex: "storeCode",
      key: "storeCode",
      render: (storeCode: string, record: StoreDisplayGroupPlanogram) => (
        <div>
          <div>{storeCode}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {record.storeName}
          </div>
        </div>
      ),
      width: 150,
    },
    {
      title: t("storeDisplayGroup.shopGroup"),
      dataIndex: "shopGroupCode",
      key: "shopGroupCode",
      render: (shopGroupCode: string, record: StoreDisplayGroupPlanogram) => (
        <div>
          <div>{shopGroupCode}</div>
          {record.shopGroupName && (
            <div style={{ fontSize: "12px", color: "#666" }}>
              {record.shopGroupName}
            </div>
          )}
        </div>
      ),
      width: 150,
    },
    {
      title: t("storeDisplayGroup.displayGroup"),
      dataIndex: "displayGroupCode",
      key: "displayGroupCode",
      render: (
        displayGroupCode: string,
        record: StoreDisplayGroupPlanogram
      ) => (
        <div>
          <div>{displayGroupCode}</div>
          {record.displayGroupName && (
            <div style={{ fontSize: "12px", color: "#666" }}>
              {record.displayGroupName}
            </div>
          )}
        </div>
      ),
      width: 150,
    },
    {
      title: t("storeDisplayGroupPlanogram.source"),
      dataIndex: "source",
      key: "source",
      render: (source: string) => (
        <Tag color={source === "benchmark" ? "blue" : "green"}>
          {t(`storeDisplayGroupPlanogram.sourceType.${source}`)}
        </Tag>
      ),
      width: 150,
    },
    {
      title: t("storeDisplayGroupPlanogram.itemCount"),
      dataIndex: "itemCount",
      key: "itemCount",
      // sorter: true,
      sortOrder:
        pagination.sortBy === "itemCount"
          ? pagination.sortOrder === "asc"
            ? "ascend"
            : "descend"
          : null,
      width: 100,
      align: "center",
    },
    {
      title: t("storeDisplayGroupPlanogram.benchmarkImage"),
      dataIndex: "imageUrl",
      key: "imageUrl",
      render: (imageUrl: string) =>
        imageUrl ? (
          <Image
            src={imageUrl + "?x-oss-process=image/resize,w_200,h_150"}
            width={60}
            height={40}
            style={{ objectFit: "cover", borderRadius: "4px" }}
            fallback={imageUrl}
            preview={
              imageUrl && {
                src: imageUrl,
              }
            }
          />
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
      width: 100,
      align: "center",
    },
    {
      title: t('storeDisplayGroupPlanogram.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusColors = {
          '草稿': 'default',
          '审核中': 'processing',
          '审核拒绝': 'error',
          '生效中': 'success',
          '已归档': 'warning'
        };
        return (
          <Tag color={statusColors[status as keyof typeof statusColors] || 'default'}>
            {t(`storeDisplayGroupPlanogram.statusType.${getStatusKey(status)}`) || status}
          </Tag>
        );
      },
      width: 100,
      align: 'center',
    },
    {
      title: t("storeDisplayGroupPlanogram.createTime"),
      dataIndex: "gmtCreate",
      key: "gmtCreate",
      // sorter: true,
      sortOrder:
        pagination.sortBy === "gmtCreate"
          ? pagination.sortOrder === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: formatDateTime,
      width: 180,
    },
    {
      title: t("storeDisplayGroupPlanogram.updateTime"),
      dataIndex: "gmtModified",
      key: "gmtModified",
      // sorter: true,
      sortOrder:
        pagination.sortBy === "gmtModified"
          ? pagination.sortOrder === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: formatDateTime,
      width: 180,
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            title={t("common.view")}
          />
        </Space>
      ),
    },
  ];

  // 显示错误状态
  if (error && isRetryableError({ message: error })) {
    return (
      <div className="page-container">
        <NetworkError onRetry={handleRetry} message={error} />
      </div>
    );
  }

  return (
    <div>
      {/* 筛选面板 */}
      <FilterPanel
        onFilter={handleFiltersChange}
        onReset={handleReset}
        // initialValues={filters}
        title={t("menu.planogramList")}
      >
        <Col span={6}>
          <Form.Item name="storeCode" label={t("storeDisplayGroup.shop")}>
            <Select
              placeholder={t(
                "storeDisplayGroupPlanogram.filter.storePlaceholder"
              )}
              allowClear
              showSearch
              // filterOption={(input, option) =>
              //   (option?.label as string)
              //     ?.toLowerCase()
              //     .includes(input.toLowerCase())
              // }
            >
              <Select.Option value="all">{t("common.all")}</Select.Option>
              {storeOptions.map((store) => (
                <Select.Option key={store.code} value={store.code}>
                  {store.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="shopGroupCode"
            label={t("storeDisplayGroup.shopGroup")}
          >
            <Select
              placeholder={t(
                "storeDisplayGroupPlanogram.filter.storeGroupPlaceholder"
              )}
              allowClear
              showSearch
              // filterOption={(input, option) =>
              //   (option?.label as string)
              //     ?.toLowerCase()
              //     .includes(input.toLowerCase())
              // }
            >
              <Select.Option value="all">{t("common.all")}</Select.Option>
              {storeGroupOptions.map((group) => (
                <Select.Option key={group.code} value={group.code}>
                  {group.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="displayGroupCode"
            label={t("storeDisplayGroup.displayGroup")}
          >
            <Select
              placeholder={t(
                "storeDisplayGroupPlanogram.filter.displayGroupPlaceholder"
              )}
              allowClear
              showSearch
              // filterOption={(input, option) =>
              //   (option?.label as string)
              //     ?.toLowerCase()
              //     .includes(input.toLowerCase())
              // }
            >
              <Select.Option value="all">{t("common.all")}</Select.Option>
              {displayGroupOptions.map((dg) => (
                <Select.Option key={dg.code} value={dg.code}>
                  {dg.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="division" label={t("storeDisplayGroup.division")}>
            <Select
              placeholder={t(
                "storeDisplayGroupPlanogram.filter.divisionPlaceholder"
              )}
              allowClear
              showSearch
              // filterOption={(input, option) =>
              //   (option?.label as string)
              //     ?.toLowerCase()
              //     .includes(input.toLowerCase())
              // }
            >
              <Select.Option value="all">{t("common.all")}</Select.Option>
              {divisionOptions.map((division) => (
                <Select.Option key={division.value} value={division.value}>
                  {division.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={planogramData}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t("storeDisplayGroupPlanogram.pagination.total", {
              start: range[0],
              end: range[1],
              total,
            }),
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200, y: 500 }}
        locale={{
          emptyText:
            planogramData.length === 0 && !loading ? (
              <EmptyState
                type={
                  Object.keys(filters).some(
                    (key) =>
                      filters[key as keyof StoreDisplayGroupPlanogramFilters]
                  )
                    ? "filter"
                    : "default"
                }
                onAction={
                  Object.keys(filters).some(
                    (key) =>
                      filters[key as keyof StoreDisplayGroupPlanogramFilters]
                  )
                    ? handleReset
                    : undefined
                }
                actionText={
                  Object.keys(filters).some(
                    (key) =>
                      filters[key as keyof StoreDisplayGroupPlanogramFilters]
                  )
                    ? t("common.reset")
                    : undefined
                }
              />
            ) : undefined,
        }}
      />
    </div>
  );
};

export default StoreDisplayGroupPlanogramList;
